/**
 * FullTimersTab — Finance sub-tab for full-time (is_primary=true) players'
 * season fees. Only rendered when teams.settings.season_fee_required ===
 * true (gated by the FinancePage shell, not here).
 *
 * 2026-07-11 (batch-B G1): shipped as a READ-ONLY status view (amount +
 * paid/outstanding per player), computed client-side via an O(N) fan-out
 * over GET /payments/team/:teamId/finance/player/:userId (payments.js was
 * out of scope for that batch).
 *
 * 2026-07-11 (batch-B G7, same day): upgraded to a real collection flow —
 *   • Backed by ONE call to the new GET /payments/team/:teamId/finance/
 *     season-fees aggregate (backend/src/routes/payments.js) instead of
 *     the fan-out — see fullTimersData.js's loadSeasonFeeSummary().
 *   • Header shows the configured fee, full-time player count, collected/
 *     outstanding totals, and — when Team Settings → Pricing has
 *     "pass fee to player" ON — an ESTIMATED player-facing total (fee +
 *     processing), explicitly labeled an estimate. The real charge is
 *     always computed fresh at checkout (POST /payments/checkout).
 *   • Per-player "Request payment" — POST .../finance/season-fee-request
 *     (emails a new dedicated template + an in-app notification, each
 *     independently gated on the player's own notification prefs).
 *   • Per-player "💵 Cash" — opens a small modal, POSTs
 *     .../finance/season-fee-cash-payment (no game involved, unlike the
 *     Spares tab's cash flow — see that endpoint's comment for why this
 *     writes into `payments` directly instead of spare_game_plays).
 */
import { useState, useEffect, useCallback } from 'react';
import { CheckCircle, AlertTriangle, Mail, DollarSign } from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import { loadSeasonFeeSummary } from './fullTimersData.js';

const fmtMoney = (cents, currency = 'cad') => {
  const amount = (cents || 0) / 100;
  const ccy = (currency || 'cad').toUpperCase();
  return `${ccy} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtWhen = (v) => {
  if (!v) return null;
  try { return new Date(v).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return null; }
};

export default function FullTimersTab({ teamId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState(null); // the season-fees aggregate
  const [error, setError]     = useState(false);
  const [requestingId, setRequestingId] = useState(null); // userId currently sending a request
  const [cashUser, setCashUser] = useState(null); // { userId, name, owedCents } or null

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const d = await loadSeasonFeeSummary(teamId);
      setData(d);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  const requestPayment = async (p) => {
    setRequestingId(p.userId);
    try {
      const { data: r } = await api.post(`/payments/team/${teamId}/finance/season-fee-request`, { userId: p.userId });
      const channels = [r.emailed && 'email', r.pushed && 'notification'].filter(Boolean).join(' + ') || 'nowhere (no channel available)';
      toast.success(`Payment request sent to ${p.name} via ${channels}`);
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Failed to send request');
    } finally {
      setRequestingId(null);
    }
  };

  if (loading) return <p className="text-secondary text-sm py-6 text-center">Loading full-time player fees…</p>;
  if (error || !data) return <p className="text-secondary text-sm py-6 text-center">Couldn't load season-fee data.</p>;

  const { feeCents, currency, passToPlayer, estimatedProcessingCents, estimatedPlayerTotalCents, players = [], summary = {} } = data;
  const hasFeeAmount = Number(feeCents) > 0;

  return (
    <div>
      <p className="text-secondary text-sm mb-4" style={{ lineHeight: 1.5 }}>
        Full-time players (skaters — goalies excluded) and their season fee status. "Request payment" emails a Pay
        Now link (and sends an in-app notification); "💵 Cash" records an in-person payment.
      </p>

      {/* ── Setup / summary header ── */}
      <div className="card mb-5">
        <h3 className="font-semibold text-sm mb-3">Season fee</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-3)', letterSpacing: '0.06em', fontWeight: 600 }}>Fee</div>
            <div className="font-semibold mt-0.5">{hasFeeAmount ? fmtMoney(feeCents, currency) : 'Not set'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-3)', letterSpacing: '0.06em', fontWeight: 600 }}>Full-time players</div>
            <div className="font-semibold mt-0.5">{summary.count || 0}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-3)', letterSpacing: '0.06em', fontWeight: 600 }}>Collected</div>
            <div className="font-semibold mt-0.5" style={{ color: 'var(--goal)' }}>{fmtMoney(summary.collectedCents, currency)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-3)', letterSpacing: '0.06em', fontWeight: 600 }}>Outstanding</div>
            <div className="font-semibold mt-0.5" style={{ color: summary.outstandingCents > 0 ? 'var(--puck)' : 'var(--text-2)' }}>
              {hasFeeAmount ? fmtMoney(summary.outstandingCents, currency) : '—'}
            </div>
          </div>
        </div>

        {!hasFeeAmount && (
          <p className="text-xs mt-3" style={{ color: 'var(--puck)', lineHeight: 1.5 }}>
            ⚠ No season fee amount is configured yet — set it in Team Settings → Pricing to see
            paid/outstanding status per player.
          </p>
        )}

        {/* Player-facing estimate — only shown when the coach passes the
            fee (+ processing) on to the player, and only when it actually
            adds something on top of the base fee. */}
        {hasFeeAmount && passToPlayer && estimatedProcessingCents > 0 && (
          <div className="mt-3 pt-3" style={{ borderTop: '1px dashed var(--border)' }}>
            <p className="text-xs" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
              Players paying online see an <strong>estimated</strong> total of{' '}
              <strong style={{ color: 'var(--text)' }}>{fmtMoney(estimatedPlayerTotalCents, currency)}</strong>{' '}
              ({fmtMoney(feeCents, currency)} fee + ≈{fmtMoney(estimatedProcessingCents, currency)} processing).
              This is an estimate — Stripe's exact cut can vary slightly; the real amount is shown at checkout.
            </p>
          </div>
        )}
      </div>

      <div className="card">
        <div className="mb-3">
          <h3 className="font-semibold text-sm">Full-time players</h3>
        </div>
        {players.length === 0 ? (
          <p className="text-secondary text-sm py-4 text-center">
            No full-time players on the roster yet.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="w-full text-sm" style={{ minWidth: 640 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th className="text-left py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Player</th>
                  <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Paid</th>
                  <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Status</th>
                  <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}></th>
                </tr>
              </thead>
              <tbody>
                {players.map(p => {
                  let statusLabel = 'Not collected yet';
                  let statusColor = 'var(--text-2)';
                  let StatusIcon = null;
                  if (p.status === 'paid') { statusLabel = 'Paid'; statusColor = 'var(--goal)'; StatusIcon = CheckCircle; }
                  else if (p.status === 'partial') { statusLabel = 'Partial'; statusColor = 'var(--puck)'; StatusIcon = AlertTriangle; }
                  const lastWhen = fmtWhen(p.lastPaymentAt);
                  const owedCents = hasFeeAmount ? Math.max(0, feeCents - (p.paidCents || 0)) : 0;
                  return (
                    <tr key={p.userId} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td className="py-2 px-2">
                        <div className="font-medium" style={{ color: 'var(--ice)' }}>{p.name}</div>
                        <div className="text-xs" style={{ color: 'var(--text-3)' }}>{p.email}</div>
                      </td>
                      <td className="py-2 px-2 text-right">
                        {fmtMoney(p.paidCents, currency)}
                        {hasFeeAmount && <span className="text-xs" style={{ color: 'var(--text-3)' }}> / {fmtMoney(feeCents, currency)}</span>}
                      </td>
                      <td className="py-2 px-2 text-right" style={{ color: statusColor, fontWeight: 700 }}>
                        <span className="inline-flex items-center gap-1" style={{ justifyContent: 'flex-end' }}>
                          {StatusIcon && <StatusIcon size={13} />}
                          {statusLabel}
                        </span>
                        {lastWhen && <div className="text-xs font-normal" style={{ color: 'var(--text-3)' }}>Last paid {lastWhen}</div>}
                      </td>
                      <td className="py-2 px-2 text-right" style={{ whiteSpace: 'nowrap' }}>
                        {p.status !== 'paid' && hasFeeAmount && (
                          <>
                            <button
                              type="button"
                              className="text-xs px-2 py-1"
                              style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', color: 'var(--text-2)' }}
                              disabled={requestingId === p.userId}
                              onClick={() => requestPayment(p)}>
                              <Mail size={12} style={{ verticalAlign: -1, marginRight: 3 }} />
                              {requestingId === p.userId ? 'Sending…' : 'Request payment'}
                            </button>
                            <button
                              type="button"
                              className="text-xs px-2 py-1 ml-1"
                              style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', color: 'var(--goal)' }}
                              onClick={() => setCashUser({ userId: p.userId, name: p.name, owedCents: owedCents || feeCents })}>
                              💵 Cash
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cashUser && (
        <SeasonFeeCashModal
          teamId={teamId}
          userId={cashUser.userId}
          name={cashUser.name}
          owedCents={cashUser.owedCents}
          currency={currency}
          onClose={() => setCashUser(null)}
          onRecorded={load}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SeasonFeeCashModal — G7 (2026-07-11). Coach records an out-of-system
// (cash) SEASON FEE payment for one full-time player. Simpler than
// SparesTab's CashPaymentModal (no per-game list — a season fee is one
// lump sum) but same visual language: amount prefilled from what's still
// owed, an optional note, a single POST.
// ─────────────────────────────────────────────────────────────
function SeasonFeeCashModal({ teamId, userId, name, owedCents, currency, onClose, onRecorded }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [amount, setAmount] = useState(((owedCents || 0) / 100).toFixed(2));
  const [note, setNote]     = useState('');
  const [submitting, setSubmitting] = useState(false);

  const parseCents = (str) => {
    const n = parseFloat(str);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  };

  const submit = async () => {
    const cents = parseCents(amount);
    if (cents == null || cents <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/payments/team/${teamId}/finance/season-fee-cash-payment`, {
        userId, amountCents: cents, note: note.trim() || undefined,
      });
      toast.success(`Recorded ${name}'s cash season fee payment 💵`);
      onRecorded();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--border)',
          width: '100%', maxWidth: 420, boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
          // 2026-07-14 (mobile pass) — capped + scrollable + flex-column,
          // matching SparesTab's CashPaymentModal fix. This modal's Amount
          // field is autoFocus, so the keyboard is up from the moment it
          // opens; without a height cap the vh-based centering could leave
          // the "Record payment" footer button under the keyboard on short
          // phones. var(--mobile-h) falls back to 100dvh on desktop, so
          // desktop rendering is unchanged.
          maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)',
          display: 'flex', flexDirection: 'column',
        }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: '1px solid var(--border)', flexShrink: 0,
        }}>
          <div>
            <h2 className="font-bold text-base">💵 Record cash season fee</h2>
            <p className="text-xs" style={{ color: 'var(--text-3)' }}>{name}</p>
          </div>
          <button
            type="button" onClick={onClose} aria-label="Close"
            style={{
              background: 'transparent', border: '1px solid var(--border)', borderRadius: 8,
              padding: '6px 10px', cursor: 'pointer', color: 'var(--text-2)', fontSize: 14,
            }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: '14px 18px', overflowY: 'auto' }}>
          <label className="label">Amount</label>
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-2)', fontSize: 13, pointerEvents: 'none' }}>
              {(currency || 'cad').toUpperCase()}
            </span>
            <input
              type="text" inputMode="decimal" className="input-field" autoFocus
              style={{ paddingLeft: 42 }}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <label className="label">Note (optional)</label>
          <input
            type="text" maxLength={500} className="input-field"
            placeholder="e.g. paid at the rink July 10"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 18px', borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0,
        }}>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="btn-ice"
            style={{ opacity: submitting ? 0.6 : 1 }}>
            <DollarSign size={13} style={{ verticalAlign: -2, marginRight: 3 }} />
            {submitting ? 'Recording…' : 'Record payment'}
          </button>
        </div>
      </div>
    </div>
  );
}
